import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Fragment } from 'react';
import { DoubleSlider } from '@bwoty-web/ui-kit';

import RatingFilter from './RatingFilter';
import DestinationFilter from './DestinationFilter';
import FilterButtonGroup from '../../shared/filterButtonGroup/FilterButtonGroup';

import {
  cruiseCategorySelected,
  departureMonthSelected,
  destinationSelected,
  durationSelected,
  numberOfDestinationsSelected,
  shipRatingSelected,
  shippingCompanySelected,
  singleFilterCleared,
} from '../../../redux/reducers/cruiseList/filter';
import { selectNumberOfCruises } from '../../../redux/selectors/cruiseList';

import './filterGroup.scss';

// `filterActions` used to arrive pre-bound from `CruiseFilterPanel`; it is built here from
// `dispatch` instead. **The key names are load-bearing:** the shared `FilterButtonGroup` is also
// used by flight-list and package-plus-list, and the presentational siblings `RatingFilter` and
// `DestinationFilter` read the same names — so the shape cannot change here alone.
//
// The two range actions take a single `{ lowValue, highValue }` payload now, where the old
// creators took two positional arguments; the wrappers below absorb that.
const FilterGroup = ({ filterGroup, filterMatches, filterPanelIsOpen, filterStopsAllLabel }) => {
  const dispatch = useDispatch();
  const numberOfCruises = useSelector(selectNumberOfCruises);

  const filterActions = {
    clearSingleFilter: (filterGroupKey) => dispatch(singleFilterCleared(filterGroupKey)),
    selectFilterCruiseCategory: (selectedId) => dispatch(cruiseCategorySelected(selectedId)),
    selectFilterDepartureMonth: (selectedId) => dispatch(departureMonthSelected(selectedId)),
    selectFilterDestination: (selectedId) => dispatch(destinationSelected(selectedId)),
    selectFilterDuration: (lowValue, highValue) => dispatch(durationSelected({ lowValue, highValue })),
    selectFilterNumberOfDestinations: (lowValue, highValue) =>
      dispatch(numberOfDestinationsSelected({ lowValue, highValue })),
    selectFilterShippingCompany: (selectedId) => dispatch(shippingCompanySelected(selectedId)),
    selectFilterShipRating: (rating) => dispatch(shipRatingSelected(rating)),
  };

  const filterGroupMarkup = [];

  switch (filterGroup.id) {
    case 'departureMonth':
      filterGroupMarkup.push(
        <FilterButtonGroup
          allBtnTotalNumberOfItems={numberOfCruises}
          filterActions={filterActions}
          filterGroupId={filterGroup.id}
          filterMatches={filterMatches}
          filterStopsAllLabel={filterStopsAllLabel}
          handleSelect={filterActions.selectFilterDepartureMonth}
          key={filterGroup.id}
          options={filterGroup.options}
          selectedValues={filterGroup.selected}
        />,
      );
      break;
    case 'shipRating':
      filterGroupMarkup.push(
        <RatingFilter
          actions={filterActions}
          allBtnTotalNumberOfItems={numberOfCruises}
          filterStopsAllLabel={filterStopsAllLabel}
          id={filterGroup.id}
          key={filterGroup.id}
          matches={filterMatches.shipRating}
          options={filterGroup.options}
          selected={filterGroup.selected}
        />,
      );
      break;
    case 'duration':
      filterGroupMarkup.push(
        <Fragment key={filterGroup.id}>
          <h3 className="iw-cruise-filter-group__header">{filterGroup.heading}</h3>
          <DoubleSlider
            filterPluralUnitLabel={filterGroup.daysLabel}
            filterSingularUnitLabel={filterGroup.dayLabel}
            filterSliderToLabel={filterGroup.toLabel}
            hidden={!filterPanelIsOpen}
            maxValue={filterGroup.maxValue}
            minValue={filterGroup.minValue}
            onChangeComplete={(values) => filterActions.selectFilterDuration(values.lowValue, values.highValue)}
            selectedHighValue={
              filterGroup.selectedMaxValue != null ? filterGroup.selectedMaxValue : filterGroup.maxValue
            }
            selectedLowValue={
              filterGroup.selectedMinValue != null ? filterGroup.selectedMinValue : filterGroup.minValue
            }
            uniqueId={filterGroup.id}
          />
        </Fragment>,
      );
      break;
    case 'cruiseCategory':
      filterGroupMarkup.push(
        <FilterButtonGroup
          allBtnTotalNumberOfItems={numberOfCruises}
          filterActions={filterActions}
          filterGroupId={filterGroup.id}
          filterMatches={filterMatches}
          filterStopsAllLabel={filterStopsAllLabel}
          handleSelect={filterActions.selectFilterCruiseCategory}
          key={filterGroup.id}
          options={filterGroup.options}
          selectedValues={filterGroup.selected}
        />,
      );
      break;
    case 'destination':
      filterGroupMarkup.push(
        <DestinationFilter
          allBtnTotalNumberOfItems={numberOfCruises}
          filterActions={filterActions}
          filterGroup={filterGroup}
          filterMatches={filterMatches}
          filterPanelIsOpen={filterPanelIsOpen}
          filterStopsAllLabel={filterStopsAllLabel}
          key={filterGroup.id}
        />,
      );
      break;
    case 'shippingCompany':
      filterGroupMarkup.push(
        <FilterButtonGroup
          allBtnTotalNumberOfItems={numberOfCruises}
          filterActions={filterActions}
          filterGroupId={filterGroup.id}
          filterMatches={filterMatches}
          filterStopsAllLabel={filterStopsAllLabel}
          handleSelect={filterActions.selectFilterShippingCompany}
          key={filterGroup.id}
          options={filterGroup.options}
          selectedValues={filterGroup.selected}
        />,
      );
      break;
    default:
      break;
  }
  return filterGroupMarkup;
};

FilterGroup.propTypes = {
  filterGroup: PropTypes.shape({
    dayLabel: PropTypes.string,
    daysLabel: PropTypes.string,
    heading: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    maxValue: PropTypes.number,
    minValue: PropTypes.number,
    options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.shape({}), PropTypes.number])),
    order: PropTypes.number.isRequired,
    selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    selectedMaxValue: PropTypes.number,
    selectedMinValue: PropTypes.number,
    toLabel: PropTypes.string,
  }).isRequired,
  filterMatches: PropTypes.shape({
    cruiseCategory: PropTypes.shape().isRequired,
    departureMonth: PropTypes.shape().isRequired,
    destination: PropTypes.shape().isRequired,
    shippingCompany: PropTypes.shape().isRequired,
    shipRating: PropTypes.shape().isRequired,
  }).isRequired,
  filterPanelIsOpen: PropTypes.bool.isRequired,
  filterStopsAllLabel: PropTypes.string.isRequired,
};

export default FilterGroup;
